/**
 * Account refusals — the server closed THIS account's door, not the token.
 *
 * ⚠️⚠️ Measured on e2e 2026-09-26: the super admin deleted «Gestoría Albéniz
 * Asesores 135785» while its user was signed in. The API answered 403
 * `ORGANIZATION_PENDING_DELETION` and the app dropped her on /login with no
 * word of why — the login form looked like a random sign-out. Same for a
 * suspended organisation or user.
 *
 * The code survives the redirect in sessionStorage (a toast would be gone by
 * the time the login page paints) and the login page reads it once.
 */

/** Codes the auth middleware / login answer when the ACCOUNT may not proceed. */
const ACCOUNT_REFUSAL_CODES = [
  'USER_SUSPENDED',
  'ORGANIZATION_SUSPENDED',
  'ORGANIZATION_PENDING_DELETION',
  'ORG_NOT_ACTIVE',
] as const

export type AccountRefusalCode = (typeof ACCOUNT_REFUSAL_CODES)[number]

const STORAGE_KEY = 'bat:signed-out-reason'

export function isAccountRefusalCode(code: unknown): code is AccountRefusalCode {
  return typeof code === 'string' && (ACCOUNT_REFUSAL_CODES as readonly string[]).includes(code)
}

/** The refusal code carried by a 403, or null for any other failure. */
export function accountRefusalCodeOf(
  error: { status?: number; code?: string } | null | undefined,
): AccountRefusalCode | null {
  if (error?.status !== 403) return null
  return isAccountRefusalCode(error.code) ? error.code : null
}

export function rememberSignedOutReason(code: AccountRefusalCode): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, code)
  } catch {
    // Private mode / blocked storage: the toast still says it.
  }
}

/**
 * The reason left by the last sign-out, WITHOUT clearing it.
 *
 * ⚠️ Read and clear are split on purpose: React StrictMode (how the app
 * mounts) runs a `useState` initializer twice, so a read-and-clear there
 * handed the reason to the discarded call and the login page showed nothing
 * (e2e 2026-09-26). Peek in the initializer, clear in an effect.
 */
export function peekSignedOutReason(): AccountRefusalCode | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return isAccountRefusalCode(stored) ? stored : null
  } catch {
    return null
  }
}

export function clearSignedOutReason(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // nothing to clear
  }
}

/** Read AND clear, for non-React callers and tests. */
export function consumeSignedOutReason(): AccountRefusalCode | null {
  const reason = peekSignedOutReason()
  clearSignedOutReason()
  return reason
}
